import React from 'react';

export type FoodType = 'veg' | 'non-veg' | 'vegan' | 'gluten-free' | 'lactose-free' | 'dairy-free';

interface FoodTypeBadgeProps {
  type: FoodType;
  size?: number; // default 16
  className?: string;
  showTooltip?: boolean;
}

export const FoodTypeBadge: React.FC<FoodTypeBadgeProps> = ({
  type,
  size = 16,
  className = '',
  showTooltip = true
}) => {
  const getTooltip = () => {
    switch (type) {
      case 'veg':
        return 'Vegetarian (Kasvis)';
      case 'non-veg':
        return 'Non-Vegetarian (Liha/Kala)';
      case 'vegan':
        return 'Vegan (Vegaaninen 🌱)';
      case 'gluten-free':
        return 'Gluten-Free (Gluteeniton G 🌾)';
      case 'lactose-free':
        return 'Lactose-Free (Laktoositon L 🥛)';
      case 'dairy-free':
        return 'Dairy-Free / Maidoton (M)';
      default:
        return '';
    }
  };

  const title = showTooltip ? getTooltip() : undefined;

  switch (type) {
    // 1. VEGETARIAN (Classic Green Square with Solid Green Circle)
    case 'veg':
      return (
        <span
          title={title}
          className={`food-badge-frame ${className}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${size}px`,
            height: `${size}px`,
            border: '1.5px solid #10b981',
            borderRadius: '3px',
            background: 'rgba(16, 185, 129, 0.05)',
            flexShrink: 0
          }}
        >
          <span
            style={{
              width: `${Math.round(size * 0.45)}px`,
              height: `${Math.round(size * 0.45)}px`,
              borderRadius: '50%',
              background: '#10b981'
            }}
          />
        </span>
      );

    // 2. NON-VEGETARIAN (Classic Crimson Square with Solid Triangle)
    case 'non-veg':
      return (
        <span
          title={title}
          className={`food-badge-frame ${className}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${size}px`,
            height: `${size}px`,
            border: '1.5px solid #e11d48',
            borderRadius: '3px',
            background: 'rgba(225, 29, 72, 0.05)',
            flexShrink: 0
          }}
        >
          <span
            style={{
              width: 0,
              height: 0,
              borderLeft: `${Math.round(size * 0.28)}px solid transparent`,
              borderRight: `${Math.round(size * 0.28)}px solid transparent`,
              borderBottom: `${Math.round(size * 0.45)}px solid #e11d48`
            }}
          />
        </span>
      );

    // 3. VEGAN (Green Square with Organic Leaf Icon)
    case 'vegan':
      return (
        <span
          title={title}
          className={`food-badge-frame ${className}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${size}px`,
            height: `${size}px`,
            border: '1.5px solid #059669',
            borderRadius: '3px',
            background: 'rgba(5, 150, 105, 0.08)',
            color: '#059669',
            flexShrink: 0
          }}
        >
          <svg
            width={Math.round(size * 0.65)}
            height={Math.round(size * 0.65)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#059669"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" fill="#059669" fillOpacity="0.25" />
            <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
          </svg>
        </span>
      );

    // 4. GLUTEN-FREE (Cyan Blue Square with Wheat Grain Ear Icon)
    case 'gluten-free':
      return (
        <span
          title={title}
          className={`food-badge-frame ${className}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${size}px`,
            height: `${size}px`,
            border: '1.5px solid #0284c7',
            borderRadius: '3px',
            background: 'rgba(2, 132, 199, 0.08)',
            color: '#0284c7',
            flexShrink: 0
          }}
        >
          <svg
            width={Math.round(size * 0.7)}
            height={Math.round(size * 0.7)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#0284c7"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 22 12 12" />
            <path d="M7 11.5a3 3 0 0 1 3-3c1.5 0 2 .5 2 2a3 3 0 0 1-3 3c-1.5 0-2-.5-2-2Z" fill="#0284c7" fillOpacity="0.3" />
            <path d="M12.5 6a3 3 0 0 1 3-3c1.5 0 2 .5 2 2a3 3 0 0 1-3 3c-1.5 0-2-.5-2-2Z" fill="#0284c7" fillOpacity="0.3" />
            <path d="M15 15.5a3 3 0 0 1 3-3c1.5 0 2 .5 2 2a3 3 0 0 1-3 3c-1.5 0-2-.5-2-2Z" fill="#0284c7" fillOpacity="0.3" />
          </svg>
        </span>
      );

    // 5. LACTOSE-FREE (Purple Square with Milk Drop Icon)
    case 'lactose-free':
      return (
        <span
          title={title}
          className={`food-badge-frame ${className}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${size}px`,
            height: `${size}px`,
            border: '1.5px solid #7c3aed',
            borderRadius: '3px',
            background: 'rgba(124, 58, 237, 0.08)',
            color: '#7c3aed',
            flexShrink: 0
          }}
        >
          <svg
            width={Math.round(size * 0.65)}
            height={Math.round(size * 0.65)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#7c3aed"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" fill="#7c3aed" fillOpacity="0.3" />
          </svg>
        </span>
      );

    // 6. DAIRY-FREE / MAIDOTON (Teal Square with Crossed Milk Bottle/Glass Icon)
    case 'dairy-free':
      return (
        <span
          title={title}
          className={`food-badge-frame ${className}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${size}px`,
            height: `${size}px`,
            border: '1.5px solid #0d9488',
            borderRadius: '3px',
            background: 'rgba(13, 148, 136, 0.08)',
            color: '#0d9488',
            flexShrink: 0
          }}
        >
          <svg
            width={Math.round(size * 0.65)}
            height={Math.round(size * 0.65)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#0d9488"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="6" y="8" width="12" height="13" rx="2" fill="#0d9488" fillOpacity="0.25" />
            <path d="M9 8V4h6v4" />
            <line x1="3" y1="3" x2="21" y2="21" strokeWidth="2.5" />
          </svg>
        </span>
      );

    default:
      return null;
  }
};
